import { Browser } from '@capacitor/browser';
import { t } from '@transifex/native';

import {
	RefusalCode,
	withoutInvalidInputPrefix,
} from 'pkg/actions/form_event_group_waitlist';
import type {
	BasicSubmissionCreatePayload,
	CreateFormSubmissionResponse,
} from 'pkg/actions/form_submissions/interface';
import type { PaymentRow } from 'pkg/actions/payment_preview';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { isRequired } from 'pkg/api/models/agreement/guards';
import { hasStarted, isCanceled, isFull } from 'pkg/api/models/event/guards';
import { Status } from 'pkg/api/models/form_event_group';
import { isPricedPerEvent } from 'pkg/api/models/form_event_group/guards';
import { formatEventTimestamp } from 'pkg/date';
import { isApp } from 'pkg/platform';
import * as routes from 'pkg/router/routes';
import { replaceState } from 'pkg/router/state';
import { setConfirmNavigation } from 'pkg/router/utils';

import {
	FormSectionOrder,
	AgreementConsent,
	type RegistrationFormState,
} from 'routes/forms/submission/form/context';
import {
	OutputFields,
	type FormFieldV2,
	type FormProductPrice,
} from 'routes/forms/submission/form/interface';

import {
	asNumber,
	asNumberArray,
	type FormPayload,
} from 'components/form/Form';

interface SubmissionModelEndpoints {
	createModel: string;
	eligiblePackages: string;
	billingModel: string;
}

export function getSubmissionModelEndpoints(
	form: models.form.Form,
	isPreview: boolean
): SubmissionModelEndpoints {
	if (isPreview) {
		return {
			createModel: endpoints.FormSubmissions.PreviewCreateModel(form.guid),
			eligiblePackages: endpoints.FormSubmissions.PreviewListEligiblePackages(
				form.guid
			),
			billingModel: endpoints.FormSubmissions.PreviewBillingModel(form.guid),
		};
	}

	return {
		createModel: endpoints.FormSubmissions.CreateModel(form.guid),
		eligiblePackages: endpoints.FormSubmissions.ListEligiblePackages(form.guid),
		billingModel: endpoints.FormSubmissions.BillingModel(form.guid),
	};
}

export function convertFormDataToPayload(
	data: FormPayload,
	formFields: FormFieldV2[],
	form: models.form.Form
): Partial<BasicSubmissionCreatePayload> {
	let selectedPackageId = null;
	if (form.packages?.length > 0) {
		selectedPackageId = asNumber(data['selected_package']);
	}

	const bundleId = asNumber(data['selected_bundle']);
	const paymentOptionId = asNumber(data['selected_payment_option_id']);
	const values: { [key: number]: unknown } = {};
	const optionIds: { [key: number]: number[] } = {};
	const attachmentIds: { [key: number]: number[] } = {};
	const fields: { [key: string]: unknown } = {};

	let prices: FormProductPrice[] | undefined;

	const selectedPackage = form.packages?.find(
		(pkg) => pkg.id === selectedPackageId
	);
	const selectedPackageProductIds =
		selectedPackage?.products.map((p) => p.id) || [];

	Object.entries(data).forEach(([key, value]) => {
		if (
			!value ||
			key === 'selected_bundle' ||
			key === 'selected_payment_option_id'
		) {
			return;
		}

		if (key === 'package_product_quantities') {
			if (!paymentOptionId) {
				return;
			}

			prices = Object.entries(value)
				.filter(([packageProductId]) => {
					const parsedId = Number.parseInt(packageProductId.split('_')[1], 10);

					return selectedPackageProductIds.includes(parsedId);
				})
				.map(([packageProductId, qty]) => {
					const parsedId = Number.parseInt(packageProductId.split('_')[1], 10);
					const packageProduct = selectedPackage.products.find(
						(pp) => pp.id === parsedId
					);
					const paymentOption = selectedPackage.paymentOptions.find(
						(po) => po.id === paymentOptionId
					);

					const productId = packageProduct.productId;
					const productPriceId = paymentOption?.prices?.find(
						(pp) => pp.productPrice.productId === productId
					)?.productPriceId;

					return {
						productPriceId,
						qty: asNumber(qty),
						taxRateId: packageProduct.taxRateId,
						validTo: packageProduct.validTo,
					} as FormProductPrice;
				});
		}

		if (key === 'product_quantities') {
			prices = Object.entries(value).map(([productId, qty]) => {
				const assignedProductId = Number.parseInt(productId.split('_')[1], 10);

				const productPrice = form.productPrices
					.filter((pp) => pp.bundleNumber === bundleId)
					.find((pp) => pp.productPrice.productId === assignedProductId);

				return {
					productPriceId: productPrice.productPriceId,
					qty: asNumber(qty),
					taxRateId: productPrice.taxRateId,
					validTo: productPrice.validTo,
				} as FormProductPrice;
			});
			return;
		}

		const field = formFields.find(
			(field) => field?.outputKey?.toString() === key?.toString()
		);

		if (!field) return;

		switch (field.outputField) {
			case OutputFields.Values:
				handleValuesField(field.outputKey as number, value, values);
				return;
			case OutputFields.OptionIds:
				handleOptionIDsField(field.outputKey as number, value, optionIds);
				return;
			case OutputFields.AttachmentIds:
				handleAttachmentIdsField(
					field.outputKey as number,
					value,
					attachmentIds
				);
				return;
			case OutputFields.Fields:
				if (Array.isArray(value)) {
					// Only add the field if the array is not empty
					if (value.length > 0) {
						fields[field.outputKey] = value.map((v) => {
							if (typeof v === 'number') {
								return v.toString();
							}

							return v;
						});
					}
				} else {
					fields[field.outputKey] = value;
				}
				return;
		}
	});

	const result: Partial<BasicSubmissionCreatePayload> = {
		values,
		fields,
		optionIds,
		attachmentIds,
		prices,
	};

	if (selectedPackageId) {
		result.packageId = selectedPackageId;
	}

	const selectedEventGroupIds = asNumberArray(data['selected_event_group_ids']);
	if (hasEventGroups(form) && selectedEventGroupIds?.length > 0) {
		result.eventGroupIds = selectedEventGroupIds;

		const eventGroupEvents = chosenDatesForSelection(
			form,
			selectedEventGroupIds,
			data['event_group_events'] as unknown as Record<number, number[]>
		);

		if (Object.keys(eventGroupEvents).length > 0) {
			result.eventGroupEvents = eventGroupEvents;
		}
	}

	if (bundleId) {
		result.bundleId = bundleId;
	}

	if (paymentOptionId) {
		result.paymentOptionId = paymentOptionId;
	}

	return result;
}

const handleValuesField = (
	outputKey: number,
	value: unknown,
	values: Record<number, unknown>
) => {
	values[outputKey] = value;
};

const handleOptionIDsField = (
	outputKey: number,
	value: unknown,
	optionIds: Record<number, number[]>
) => {
	if (Array.isArray(value)) {
		optionIds[outputKey] = value.map((v) => Number.parseInt(v, 10));
	} else {
		optionIds[outputKey] = [asNumber(value)];
	}
};

const handleAttachmentIdsField = (
	outputKey: number,
	value: unknown,
	attachmentIds: Record<number, number[]>
) => {
	if (Array.isArray(value)) {
		attachmentIds[outputKey] = value.map((v) => Number.parseInt(v, 10));
	} else {
		attachmentIds[outputKey] = [asNumber(value)];
	}
};

export const handleSubmissionSuccess = async (
	response: CreateFormSubmissionResponse,
	form: models.form.Form,
	group: models.group.Group
) => {
	const { checkoutUrl, formSubmission } = response;

	const paymentStatusUrl =
		routes.Registration.PaymentStatus(group.slug, form.guid) +
		`?formSubmissionId=${formSubmission.id}` +
		paymentStatusOrderParam(response);

	const successUrl = routes.Registration.Success(
		group.slug,
		form.guid,
		response.formSubmission.id
	);

	if (isApp() && checkoutUrl) {
		await Browser.open({ url: checkoutUrl });
		Browser.addListener('browserFinished', () => {
			replaceState(paymentStatusUrl);
		});
		return;
	}

	if (checkoutUrl) {
		window.location.assign(checkoutUrl);
		return;
	}

	replaceState(successUrl);
};

// The payment-status page resolves the order through the public endpoint keyed by ?o=. The checkout
// service appends it on its own returns; the in-app browser return has to carry it itself.
function paymentStatusOrderParam({
	formSubmission,
	checkoutUrl,
}: CreateFormSubmissionResponse): string {
	const orderPublicId =
		formSubmission.order?.publicId ??
		(checkoutUrl
			? new URL(checkoutUrl, window.location.origin).searchParams.get('o')
			: null);

	return orderPublicId ? `&o=${encodeURIComponent(orderPublicId)}` : '';
}

/**
 * Whether the event group step applies to this form.
 */
export function hasEventGroups(form: models.form.Form): boolean {
	return (form.eventGroups?.length ?? 0) > 0;
}

export function eventGroupChoosesDates(
	form: models.form.Form,
	eventGroup: models.formEventGroup.FormEventGroup
): boolean {
	return form.selectDatesEnabled && eventGroup.registrantsChooseDates;
}

export function confirmLeavingForm(): void {
	setConfirmNavigation(
		t('Are you sure you want to leave without completing the form?', {
			_context: 'registrations',
		})
	);
}

export function bookableInvitedEventGroupIds(
	form: models.form.Form,
	invitedEventGroupIds: number[]
): number[] {
	return (form.eventGroups ?? [])
		.filter(
			(eventGroup) =>
				invitedEventGroupIds.includes(eventGroup.id) &&
				eventGroup.availability?.status === Status.Upcoming
		)
		.map((eventGroup) => eventGroup.id);
}

export function preselectedInvitedEventGroupIds(
	form: models.form.Form,
	invitedEventGroupIds: number[],
	excludedEventGroupIds: number[]
): number[] {
	return (form.eventGroups ?? [])
		.filter(
			(eventGroup) =>
				invitedEventGroupIds.includes(eventGroup.id) &&
				!eventGroup.availability?.full &&
				!excludedEventGroupIds.includes(eventGroup.id)
		)
		.map((eventGroup) => eventGroup.id);
}

const WAITLIST_REFUSAL_CODES: string[] = [
	RefusalCode.InviteRequired,
	RefusalCode.InvitedFull,
];

export function waitlistRefusalMessage(
	form: models.form.Form,
	error = '',
	code = '',
	eventGroupId?: number
): string {
	if (!WAITLIST_REFUSAL_CODES.includes(code)) {
		return '';
	}

	const program = (form.eventGroups ?? []).find(
		(eventGroup) => eventGroup.id === eventGroupId
	)?.name;

	if (!program) {
		return withoutInvalidInputPrefix(error);
	}

	if (code === RefusalCode.InvitedFull) {
		return t(
			"{program} is full. A waitlist invite doesn't hold a spot, and the last one has been booked.",
			{ program }
		);
	}

	return t(
		'{program} has a waitlist, so only invited registrants can book it.',
		{ program }
	);
}

function chosenDatesForSelection(
	form: models.form.Form,
	selectedEventGroupIds: number[],
	chosenFormEventIds: Record<number, number[]> = {}
): Record<number, number[]> {
	const result: Record<number, number[]> = {};

	for (const eventGroup of form.eventGroups ?? []) {
		if (
			selectedEventGroupIds.includes(eventGroup.id) &&
			eventGroupChoosesDates(form, eventGroup)
		) {
			result[eventGroup.id] = chosenFormEventIds[eventGroup.id] ?? [];
		}
	}

	return result;
}

export function selectionMissingDates(
	form: models.form.Form,
	selectedEventGroupIds: number[],
	chosenFormEventIds: Record<number, number[]>
): boolean {
	const chosen = chosenDatesForSelection(
		form,
		selectedEventGroupIds,
		chosenFormEventIds
	);

	return Object.values(chosen).some((ids) => ids.length === 0);
}

export function isDateUnavailable(
	formEvent: models.formEvent.FormEvent,
	unavailableFormEventIds: number[] = []
): boolean {
	return (
		!formEvent.event ||
		unavailableFormEventIds.includes(formEvent.id) ||
		hasStarted(formEvent.event) ||
		isCanceled(formEvent.event) ||
		isFull(formEvent.event)
	);
}

export function selectionHasUnavailableDates(
	form: models.form.Form,
	selectedEventGroupIds: number[],
	chosenFormEventIds: Record<number, number[]>,
	unavailableFormEventIds: number[]
): boolean {
	const chosen = chosenDatesForSelection(
		form,
		selectedEventGroupIds,
		chosenFormEventIds
	);

	return (form.eventGroups ?? []).some((eventGroup) =>
		(chosen[eventGroup.id] ?? []).some((formEventId) => {
			const formEvent = eventGroup.events?.find(
				(link) => link.id === formEventId
			);

			return (
				!formEvent || isDateUnavailable(formEvent, unavailableFormEventIds)
			);
		})
	);
}

export function withoutFormEventIds(
	chosenFormEventIds: Record<number, number[]>,
	formEventIds: number[]
): Record<number, number[]> {
	return Object.fromEntries(
		Object.entries(chosenFormEventIds).map(([eventGroupId, ids]) => [
			eventGroupId,
			ids.filter((id) => !formEventIds.includes(id)),
		])
	);
}

export function formEventDatesLabel(
	form: models.form.Form,
	formEventIds: number[]
): string {
	return (form.eventGroups ?? [])
		.flatMap((eventGroup) => eventGroup.events ?? [])
		.filter((link) => !!link.event && formEventIds.includes(link.id))
		.sort((a, b) => a.event.startsAt - b.event.startsAt)
		.map((link) => {
			const { date, time } = formatEventTimestamp(link.event.startsAt);

			return `${date} ${time}`;
		})
		.join(', ');
}

/**
 * The selected event groups that carry a price the order will include, in the
 * form's order. Drives the cart preview lines and whether the order summary
 * shows on Billing.
 */
export function getSelectedPricedEventGroups(
	form: models.form.Form,
	selectedEventGroupIds?: number[] | null
): models.formEventGroup.FormEventGroup[] {
	if (!selectedEventGroupIds?.length) {
		return [];
	}

	return (form.eventGroups ?? []).filter(
		(candidate) =>
			!!candidate.productPriceId && selectedEventGroupIds.includes(candidate.id)
	);
}

export function getEventGroupQuantity(
	form: models.form.Form,
	eventGroup: models.formEventGroup.FormEventGroup,
	chosenFormEventIds: Record<number, number[]> = {}
): number {
	if (!isPricedPerEvent(eventGroup)) {
		return 1;
	}

	if (eventGroupChoosesDates(form, eventGroup)) {
		return chosenFormEventIds[eventGroup.id]?.length ?? 0;
	}

	const upcoming = (eventGroup.events ?? []).filter(
		(link) => !!link.event && !hasStarted(link.event) && !isCanceled(link.event)
	);

	return Math.max(upcoming.length, 1);
}

/**
 * The payment preview rows for the selected priced event groups, mirroring how
 * the backend adds them to the order: one row per group with the price itself,
 * the group's quantity (its chosen dates when priced per event, otherwise 1),
 * and the price's own tax rate. A per-event group with no dates picked yet has
 * nothing to charge and is left out.
 */
export function getEventGroupPaymentRows(
	form: models.form.Form,
	selectedEventGroupIds?: number[] | null,
	chosenFormEventIds: Record<number, number[]> = {}
): PaymentRow[] {
	return getSelectedPricedEventGroups(form, selectedEventGroupIds).flatMap(
		(eventGroup) => {
			const quantity = getEventGroupQuantity(
				form,
				eventGroup,
				chosenFormEventIds
			);

			if (quantity === 0) {
				return [];
			}

			const row: PaymentRow = {
				productPriceId: eventGroup.productPriceId,
				quantity,
			};

			if (eventGroup.productPrice?.taxRateId) {
				row.taxRateId = eventGroup.productPrice.taxRateId;
			}

			return [row];
		}
	);
}

/**
 * Whether the agreements step applies to this form.
 */
export function hasAgreements(form: models.form.Form): boolean {
	return (form.formAgreements?.length ?? 0) > 0;
}

/**
 * Returns true when the given section should be skipped for this form, e.g. the
 * Agreements step when the form has no agreements.
 */
function shouldSkipSection(
	form: models.form.Form,
	section: FormSectionOrder,
	selectedEventGroupIds?: number[] | null
): boolean {
	if (section === FormSectionOrder.EventGroups) {
		return !hasEventGroups(form);
	}

	if (section === FormSectionOrder.Agreements) {
		return !hasAgreements(form);
	}

	// The products/packages step applies to forms that offer either, and also to
	// a form whose only payable items are the selected activities — that form
	// has no products or packages, but still needs somewhere to show the cart
	// and take a discount code before Billing.
	if (section === FormSectionOrder.ProductsOrPackages) {
		return !(
			form.productPrices?.length > 0 ||
			form.packages?.length > 0 ||
			getSelectedPricedEventGroups(form, selectedEventGroupIds).length > 0
		);
	}

	// The package payment option step only applies to forms that offer packages.
	if (section === FormSectionOrder.PackagePaymentOption) {
		return !(form.packages?.length > 0);
	}

	return false;
}

/**
 * Walks the section order in the given direction (+1 forward, -1 back),
 * skipping any sections that don't apply to this form (e.g. Agreements when there
 * are no agreements).
 */
function stepSection(
	form: models.form.Form,
	current: FormSectionOrder,
	direction: 1 | -1,
	selectedEventGroupIds?: number[] | null
): FormSectionOrder {
	let next = current + direction;

	while (shouldSkipSection(form, next, selectedEventGroupIds)) {
		next += direction;
	}

	return next;
}

export function getNextSection(
	form: models.form.Form,
	current: FormSectionOrder,
	selectedEventGroupIds?: number[] | null
): FormSectionOrder {
	return stepSection(form, current, 1, selectedEventGroupIds);
}

export function getPrevSection(
	form: models.form.Form,
	current: FormSectionOrder,
	selectedEventGroupIds?: number[] | null
): FormSectionOrder {
	return stepSection(form, current, -1, selectedEventGroupIds);
}

/**
 * The first applicable section for a form: EventGroups when the form has any,
 * otherwise Fields. Used for the initial wizard section and to decide when the
 * Back button should be hidden.
 */
export function getFirstSection(form: models.form.Form): FormSectionOrder {
	return hasEventGroups(form)
		? FormSectionOrder.EventGroups
		: FormSectionOrder.Fields;
}

/**
 * The last applicable section for a form. Forms with something to pay for
 * (products, packages or a priced event group) end on Billing; otherwise the
 * final step is whatever comes last among Fields and Agreements. Used to decide
 * when the primary button should submit rather than advance.
 *
 * Once the user has selected event groups, only the selected groups' prices
 * count — a free-only selection on a mixed free/paid form must not route to
 * Billing. Before any selection, any priced group counts.
 */
export function getLastSection(
	form: models.form.Form,
	selectedEventGroupIds?: number[] | null
): FormSectionOrder {
	const hasPricedEventGroup = form.eventGroups?.some(
		(group) =>
			!!group.productPriceId &&
			(!selectedEventGroupIds?.length ||
				selectedEventGroupIds.includes(group.id))
	);

	if (
		form.productPrices?.length > 0 ||
		form.packages?.length > 0 ||
		hasPricedEventGroup
	) {
		return FormSectionOrder.Billing;
	}

	return hasAgreements(form)
		? FormSectionOrder.Agreements
		: FormSectionOrder.Fields;
}

export function isLastSection(
	form: models.form.Form,
	section: FormSectionOrder,
	selectedEventGroupIds?: number[] | null
): boolean {
	return section === getLastSection(form, selectedEventGroupIds);
}

/**
 * Whether a single agreement has been fully actioned. This is the canonical
 * rule shared by the step's Next gating, the X/Y progress count, and each
 * card's checkmark, so the three can't drift:
 *   - Declined is valid only for optional agreements.
 *   - Accepted always requires a typed signature.
 */
export function isAgreementActioned(
	link: models.agreement.FormAgreementLink,
	agreementConsents: Record<number, AgreementConsent>,
	agreementSignatures: Record<number, string>
): boolean {
	const consent = agreementConsents[link.agreementId];

	if (consent === AgreementConsent.Declined) {
		return !isRequired(link);
	}

	return (
		consent === AgreementConsent.Accepted &&
		!!agreementSignatures[link.agreementId]?.trim()
	);
}

/**
 * The agreements step is complete only when every agreement has been actioned.
 */
export function allAgreementsActioned(
	form: models.form.Form,
	agreementConsents: Record<number, AgreementConsent>,
	agreementSignatures: Record<number, string>
): boolean {
	return (form.formAgreements ?? []).every((link) =>
		isAgreementActioned(link, agreementConsents, agreementSignatures)
	);
}

export function awaitingCaptcha(state: RegistrationFormState): boolean {
	return state.requiresCaptcha && !state.captchaVerified;
}

/**
 * Whether the admin previewing the form has chosen to skip required questions.
 * Never true outside preview, so the real registration flow always validates.
 */
export function skipsRequiredInPreview(state: RegistrationFormState): boolean {
	return !!state.isPreview && !!state.skipRequired;
}

export async function validateFieldsStep(
	state: RegistrationFormState,
	trigger: () => Promise<boolean>
): Promise<boolean> {
	return skipsRequiredInPreview(state) || trigger();
}

export function agreementsStepComplete(
	form: models.form.Form,
	state: RegistrationFormState
): boolean {
	return (
		skipsRequiredInPreview(state) ||
		allAgreementsActioned(
			form,
			state.agreementConsents,
			state.agreementSignatures
		)
	);
}

export function selectedPackageHasProducts(
	form: models.form.Form,
	selectedFormPackageId: number
): boolean {
	if (!form?.packages?.length || !selectedFormPackageId) return false;

	const pkg = form.packages.find((p) => p.id === selectedFormPackageId);

	return (pkg?.products?.length ?? 0) > 0;
}

export function selectedPackageHasPaymentOptions(
	form: models.form.Form,
	selectedFormPackageId: number
): boolean {
	if (!form?.packages?.length || !selectedFormPackageId) return false;

	const pkg = form.packages.find((p) => p.id === selectedFormPackageId);

	return (pkg?.paymentOptions?.length ?? 0) > 0;
}
